import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { signageDomain } from '../app/util/domain.js';

// Create a custom axios instance for auth requests
const authAxios = axios.create({
  baseURL: signageDomain,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check for existing session on app load
    const verifySession = async () => {
      setLoading(true);
      try {
        // Check localStorage for token
        const savedToken = localStorage.getItem('digitalSignageToken');
        const savedUser = localStorage.getItem('digitalSignageUser');

        if (savedToken && savedUser) {
          // Set token in axios default headers
          authAxios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

          // Verify token with backend
          const response = await authAxios.post('/api/auth/verify');

          if (response.data.success) {
            setUser(response.data.user);
            setToken(savedToken);
          } else {
            // Clear invalid session
            localStorage.removeItem('digitalSignageUser');
            localStorage.removeItem('digitalSignageToken');
            delete authAxios.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        console.error('Session verification error:', error);
        // Clear invalid session
        localStorage.removeItem('digitalSignageUser');
        localStorage.removeItem('digitalSignageToken');
        delete authAxios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    try {
      const response = await authAxios.post('/api/auth/login', {
        email: credentials.email,
        password: credentials.password
      });

      if (response.data.success) {
        const { user, token } = response.data;

        // Store token and user in localStorage
        localStorage.setItem('digitalSignageToken', token);
        localStorage.setItem('digitalSignageUser', JSON.stringify(user));

        // Set token in axios default headers
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setUser(user);
        setToken(token);

        return { success: true, user };
      } else {
        return { success: false, error: response.data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);

      // Handle different error cases
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.error || error.response.data?.message || 'Invalid email or password';
        return { success: false, error: errorMessage };
      } else if (error.request) {
        // Request was made but no response
        return { success: false, error: 'Unable to connect to server. Please check your connection.' };
      } else {
        // Something else went wrong
        return { success: false, error: 'An unexpected error occurred. Please try again.' };
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint
      await authAxios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API response
      localStorage.removeItem('digitalSignageUser');
      localStorage.removeItem('digitalSignageToken');
      delete authAxios.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
    }
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('digitalSignageUser', JSON.stringify(updatedUser));
  };

  const refreshToken = async () => {
    try {
      const response = await authAxios.post('/api/auth/refresh');
      if (response.data.success) {
        const { token: newToken } = response.data;
        localStorage.setItem('digitalSignageToken', newToken);
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        return true;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout the user
      await logout();
      return false;
    }
  };

  // Add response interceptor to handle token expiration
  useEffect(() => {
    const interceptor = authAxios.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          const refreshed = await refreshToken();
          if (refreshed) {
            // Retry the original request
            return authAxios(error.config);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      authAxios.interceptors.response.eject(interceptor);
    };
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    hasPermission,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};